'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Server, 
  Router, 
  Wifi, 
  Shield, 
  MoreVertical, 
  ChevronLeft, 
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit2,
  Trash2,
  Power,
  Settings,
  CheckSquare,
  Square
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface Device {
  id: string;
  name: string;
  type: 'router' | 'switch' | 'firewall' | 'ap' | 'server';
  ip: string;
  mac: string;
  status: 'online' | 'offline' | 'warning';
  uptime: string;
  lastSeen: string;
  bandwidth: string;
}

const devices: Device[] = [
  { id: '1', name: '核心路由器-01', type: 'router', ip: '192.168.1.1', mac: '00:1A:2B:3C:4D:5E', status: 'online', uptime: '45天12小时', lastSeen: '刚刚', bandwidth: '1.2 Gbps' },
  { id: '2', name: '核心交换机-A', type: 'switch', ip: '192.168.1.2', mac: '00:1A:2B:3C:4D:5F', status: 'online', uptime: '30天8小时', lastSeen: '刚刚', bandwidth: '856 Mbps' },
  { id: '3', name: '核心交换机-B', type: 'switch', ip: '192.168.1.3', mac: '00:1A:2B:3C:4D:60', status: 'warning', uptime: '15天3小时', lastSeen: '2分钟前', bandwidth: '620 Mbps' },
  { id: '4', name: '防火墙-主', type: 'firewall', ip: '192.168.1.254', mac: '00:1A:2B:3C:4D:61', status: 'online', uptime: '60天0小时', lastSeen: '刚刚', bandwidth: '450 Mbps' },
  { id: '5', name: '无线AP-大厅', type: 'ap', ip: '192.168.4.1', mac: '00:1A:2B:3C:4D:62', status: 'online', uptime: '12天6小时', lastSeen: '刚刚', bandwidth: '180 Mbps' },
  { id: '6', name: '无线AP-会议室', type: 'ap', ip: '192.168.4.2', mac: '00:1A:2B:3C:4D:63', status: 'offline', uptime: '-', lastSeen: '2小时前', bandwidth: '0 Mbps' },
  { id: '7', name: '汇聚交换机-1', type: 'switch', ip: '192.168.2.1', mac: '00:1A:2B:3C:4D:64', status: 'online', uptime: '28天14小时', lastSeen: '刚刚', bandwidth: '340 Mbps' },
  { id: '8', name: '汇聚交换机-2', type: 'switch', ip: '192.168.2.2', mac: '00:1A:2B:3C:4D:65', status: 'online', uptime: '28天14小时', lastSeen: '刚刚', bandwidth: '290 Mbps' },
  { id: '9', name: '接入层-办公区A', type: 'switch', ip: '192.168.3.1', mac: '00:1A:2B:3C:4D:66', status: 'online', uptime: '20天5小时', lastSeen: '刚刚', bandwidth: '120 Mbps' },
  { id: '10', name: '接入层-办公区B', type: 'switch', ip: '192.168.3.2', mac: '00:1A:2B:3C:4D:67', status: 'online', uptime: '20天5小时', lastSeen: '刚刚', bandwidth: '95 Mbps' },
];

const deviceIcons = {
  router: Router,
  switch: Server,
  firewall: Shield,
  ap: Wifi,
  server: Server,
};

const statusConfig = {
  online: { label: '在线', color: 'bg-[var(--success)]', textColor: 'text-[var(--success)]' },
  offline: { label: '离线', color: 'bg-[var(--destructive)]', textColor: 'text-[var(--destructive)]' },
  warning: { label: '告警', color: 'bg-[var(--warning)]', textColor: 'text-[var(--warning)]' },
};

type SortField = 'name' | 'ip' | 'status' | 'uptime' | 'lastSeen';
type SortDirection = 'asc' | 'desc';

export default function DeviceTable() {
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('lastSeen');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [actionMenuOpen, setActionMenuOpen] = useState<string | null>(null);
  const itemsPerPage = 8;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedDevices = [...devices].sort((a, b) => {
    const aVal = a[sortField];
    const bVal = b[sortField];
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const totalPages = Math.ceil(sortedDevices.length / itemsPerPage);
  const paginatedDevices = sortedDevices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const toggleSelectAll = () => {
    if (selectedDevices.length === paginatedDevices.length) {
      setSelectedDevices([]);
    } else {
      setSelectedDevices(paginatedDevices.map(d => d.id));
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedDevices(prev => 
      prev.includes(id) 
        ? prev.filter(d => d !== id)
        : [...prev, id]
    );
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3.5 h-3.5 text-[var(--muted-foreground)]" />;
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-3.5 h-3.5 text-[var(--primary)]" />
      : <ArrowDown className="w-3.5 h-3.5 text-[var(--primary)]" />;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.3 }}
      className="bg-white rounded-xl border border-[var(--border)] overflow-hidden"
    >
      {/* 头部 */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center">
            <Server className="w-5 h-5 text-[var(--primary)]" />
          </div>
          <div>
            <h3 className="font-semibold text-[var(--foreground)]">设备列表</h3>
            <p className="text-xs text-[var(--muted-foreground)]">共 {devices.length} 台设备</p>
          </div>
        </div>
        
        {selectedDevices.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-[var(--muted-foreground)]">已选择 {selectedDevices.length} 项</span>
            <button className="px-3 py-1.5 text-sm text-[var(--primary)] hover:bg-[var(--primary)]/10 rounded-lg transition-colors">
              批量重启
            </button>
            <button className="px-3 py-1.5 text-sm text-[var(--destructive)] hover:bg-[var(--destructive)]/10 rounded-lg transition-colors">
              批量删除
            </button>
          </div>
        )}
      </div>

      {/* 表格 */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-[var(--secondary)]">
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={toggleSelectAll}
                  className="flex items-center justify-center w-5 h-5"
                >
                  {selectedDevices.length === paginatedDevices.length && paginatedDevices.length > 0 ? (
                    <CheckSquare className="w-4 h-4 text-[var(--primary)]" />
                  ) : (
                    <Square className="w-4 h-4 text-[var(--muted-foreground)]" />
                  )}
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('name')}
                  className="flex items-center gap-1 text-xs font-medium text-[var(--foreground)] hover:text-[var(--primary)] transition-colors"
                >
                  设备名称
                  <SortIcon field="name" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('ip')}
                  className="flex items-center gap-1 text-xs font-medium text-[var(--foreground)] hover:text-[var(--primary)] transition-colors"
                >
                  IP地址
                  <SortIcon field="ip" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('status')}
                  className="flex items-center gap-1 text-xs font-medium text-[var(--foreground)] hover:text-[var(--primary)] transition-colors"
                >
                  状态
                  <SortIcon field="status" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('uptime')}
                  className="flex items-center gap-1 text-xs font-medium text-[var(--foreground)] hover:text-[var(--primary)] transition-colors"
                >
                  运行时间
                  <SortIcon field="uptime" />
                </button>
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-[var(--foreground)]">
                带宽使用
              </th>
              <th className="px-4 py-3 text-left">
                <button 
                  onClick={() => handleSort('lastSeen')}
                  className="flex items-center gap-1 text-xs font-medium text-[var(--foreground)] hover:text-[var(--primary)] transition-colors"
                >
                  最后在线
                  <SortIcon field="lastSeen" />
                </button>
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-[var(--foreground)]">
                操作
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedDevices.map((device, index) => {
              const Icon = deviceIcons[device.type];
              const status = statusConfig[device.status];
              const isSelected = selectedDevices.includes(device.id);

              return (
                <motion.tr
                  key={device.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.03 }}
                  className={cn(
                    "border-b border-[var(--border)] last:border-0 transition-colors",
                    isSelected ? "bg-[var(--accent)]/30" : "hover:bg-[var(--secondary)]"
                  )}
                >
                  <td className="px-4 py-3">
                    <button 
                      onClick={() => toggleSelect(device.id)}
                      className="flex items-center justify-center w-5 h-5"
                    >
                      {isSelected ? (
                        <CheckSquare className="w-4 h-4 text-[var(--primary)]" />
                      ) : (
                        <Square className="w-4 h-4 text-[var(--muted-foreground)]" />
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-[var(--primary)]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-4 h-4 text-[var(--primary)]" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--foreground)]">{device.name}</p>
                        <p className="text-xs text-[var(--muted-foreground)] font-mono">{device.mac}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[var(--foreground)] font-mono">{device.ip}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-2 h-2 rounded-full",
                        status.color,
                        device.status === 'online' && "animate-pulse-slow"
                      )} />
                      <span className={cn("text-sm font-medium", status.textColor)}>
                        {status.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[var(--foreground)]">{device.uptime}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 bg-[var(--secondary)] rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-[var(--primary)] rounded-full"
                          style={{ width: device.status === 'offline' ? '0%' : `${Math.random() * 40 + 40}%` }}
                        />
                      </div>
                      <span className="text-sm text-[var(--muted-foreground)]">{device.bandwidth}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-[var(--muted-foreground)]">{device.lastSeen}</span>
                  </td>
                  <td className="px-4 py-3 relative">
                    <button 
                      onClick={() => setActionMenuOpen(actionMenuOpen === device.id ? null : device.id)}
                      className="p-2 rounded-lg hover:bg-[var(--secondary)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] transition-colors mx-auto"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                    
                    <AnimatePresence>
                      {actionMenuOpen === device.id && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.95 }}
                          className="absolute right-4 top-full mt-1 w-36 bg-white rounded-lg shadow-lg border border-[var(--border)] z-20 py-1"
                        >
                          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors">
                            <Edit2 className="w-4 h-4" />
                            编辑
                          </button>
                          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors">
                            <Settings className="w-4 h-4" />
                            配置
                          </button>
                          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--foreground)] hover:bg-[var(--secondary)] transition-colors">
                            <Power className="w-4 h-4" />
                            重启
                          </button>
                          <div className="h-px bg-[var(--border)] my-1" />
                          <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[var(--destructive)] hover:bg-[var(--destructive)]/10 transition-colors">
                            <Trash2 className="w-4 h-4" />
                            删除
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </td>
                </motion.tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* 分页 */}
      <div className="flex items-center justify-between px-5 py-4 border-t border-[var(--border)]">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--muted-foreground)]">
            显示 {(currentPage - 1) * itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, devices.length)} 条，
            共 {devices.length} 条
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:border-[var(--foreground)]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={cn(
                "w-8 h-8 rounded-lg text-sm font-medium transition-colors",
                currentPage === page
                  ? "bg-[var(--primary)] text-white"
                  : "text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:bg-[var(--secondary)]"
              )}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-lg border border-[var(--border)] text-[var(--muted-foreground)] hover:text-[var(--foreground)] hover:border-[var(--foreground)]/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}
